import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines, EASE } from "./Motion";
import { useSectionNav } from "./Header";

const D = 1.5;

const DublinClock = () => {
  const fmt = () => new Intl.DateTimeFormat("en-IE", { timeZone: "Europe/Dublin", hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date());
  const [t, setT] = useState(fmt);
  useEffect(() => {
    const iv = setInterval(() => setT(fmt()), 1000);
    return () => clearInterval(iv);
  }, []);
  return <span data-testid="hero-dublin-clock">{t}</span>;
};

const TiltFrame = ({ progress }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const scale = useTransform(progress, [0, 1], [1, 1.25]);
  const y = useTransform(progress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <motion.div
      className="relative h-full w-full [perspective:1400px]"
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
      transition={{ duration: 1.6, ease: EASE, delay: D + 0.2 }}
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, y }} className="relative h-full w-full overflow-hidden [transform-style:preserve-3d]">
        <motion.video
          data-testid="hero-video"
          style={{ scale }}
          className="h-full w-full object-cover"
          src="/assets/FRONT-PAGE.mp4"
          poster="/assets/GUESAW26WW.jpg"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0b0d]/70 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-[#f5f3ef]">
          <span>Campaign Reel</span>
          <span>SS · AW 26</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const go = useSectionNav();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-28 lg:pt-0">
      <div className="mx-auto grid min-h-[100svh] max-w-[1600px] grid-cols-1 px-6 sm:px-10 lg:grid-cols-12 lg:px-14">
        <motion.div style={{ y: textY }} className="relative z-10 flex flex-col justify-center lg:col-span-7 lg:pt-24">
          <motion.div className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-xs uppercase tracking-[0.25em] text-[#c8a97e]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D, duration: 1 }}>
            <span>Est. 1981</span>
            <span className="h-px w-8 bg-[#c8a97e]/60" />
            <span className="text-[#d6d3cd]">Dublin <DublinClock /></span>
          </motion.div>
          <h1 data-testid="hero-title" className="font-display text-[14.5vw] font-medium uppercase leading-[0.86] tracking-[-0.02em] sm:text-[13vw] lg:text-[8.6vw] 2xl:text-[150px]">
            <MaskLines delay={D} lines={["The House", <span key="g">of <em className="font-normal normal-case italic text-[#c8a97e]">Global</em></span>, "Fashion."]} />
          </h1>
          <motion.p className="mt-10 max-w-md text-base leading-relaxed text-[#c9c6c0] sm:text-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.6, duration: 1, ease: EASE }}>
            Agents for internationally recognised fashion brands in Ireland since 1981 — across wholesale, franchise and retail concepts, from our Dundrum showrooms.
          </motion.p>
          <motion.div className="mt-10 flex flex-wrap items-center gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.8, duration: 1, ease: EASE }}>
            <button data-testid="hero-explore-btn" onClick={() => go("brands")} className="group flex items-center gap-3 rounded-full bg-[#f5f3ef] px-7 py-4 text-[13px] font-medium uppercase tracking-[0.16em] text-[#0a0b0d] transition-colors duration-300 hover:bg-[#c8a97e]">
              Explore the Edit <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-45" />
            </button>
            <button data-testid="hero-book-btn" onClick={() => go("appointment")} className="link-u pb-1 text-[13px] uppercase tracking-[0.16em] text-[#f5f3ef]">
              Book a showroom visit
            </button>
          </motion.div>
        </motion.div>
        <div className="relative mt-14 h-[70vh] lg:col-span-5 lg:mt-0 lg:h-auto lg:py-28">
          <TiltFrame progress={scrollYProgress} />
        </div>
      </div>
      <motion.div className="pointer-events-none absolute bottom-8 left-1/2 hidden -translate-x-1/2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#b4b2ad] lg:block" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 1.2 }}>
        Scroll
      </motion.div>
    </section>
  );
};
